
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { cloneDeep } from 'lodash';
import draggable from 'vuedraggable';
import Toast from '@/utils/toast';
import TextButton from '@/components/shared/suites/TextButton.vue';
import CustomQuestionCard from '@/components/advanced/custom-questions/CustomQuestionCard.vue';

const namespace = 'customQuestions';

interface CustomQuestionLoading {
	save: boolean;
	deleteIndex: number;
	reorderIndex: number;
}

@Component<CustomQuestionsList>({
	components: {
		draggable,
		TextButton,
		CustomQuestionCard
	}
})
export default class CustomQuestionsList extends Vue {
	@Action('fetchCustomQuestions', { namespace }) private fetchCustomQuestions!: (type?: CustomQuestionCategoryType) => Promise<void>;
	@Action('createCustomQuestion', { namespace }) private createCustomQuestion!: (data: Partial<CustomQuestion>) => Promise<CustomQuestion | undefined>;
	@Action('updateCustomQuestion', { namespace }) private updateCustomQuestionAction!: (data: CustomQuestion) => Promise<CustomQuestion | undefined>;
	@Action('deleteCustomQuestion', { namespace }) private deleteCustomQuestionAction!: (id: number) => Promise<void>;
	@Prop({ type: Array, required: true }) private questions!: CustomQuestion[];
	@Prop({ type: String, required: true }) private type!: CustomQuestionCategoryType;
	@Prop({ type: Object, default: () => ({}) }) private selectedLocale!: RestaurantLocale;
	@Prop({ type: Boolean, default: true }) private isDefaultLocale!: boolean;
	@Prop({ type: Boolean, required: false, default: false }) private buttonDisabled!: boolean;
	@Prop({ type: String, required: false, default: 'secondary' }) private buttonType!: string;
	@Watch('questions', { deep: true })
	private onQuestionsChange(): void {
		this.mutableCustomQuestions = cloneDeep(this.questions);
	}

	private isQuestionEditing: boolean = false;
	private isOptionEditing: boolean = false;
	private mutableCustomQuestions: CustomQuestion[] = [];
	private loading: CustomQuestionLoading = {
		save: false,
		deleteIndex: -1,
		reorderIndex: -1
	};

	private get isAnyLoading(): boolean {
		return this.loading.deleteIndex > -1 || this.loading.reorderIndex > -1 || this.loading.save;
	}

	private created(): void {
		this.mutableCustomQuestions = cloneDeep(this.questions);
	}

	/**
	 * Set question editing flag to prevent other questions from being edited or deleted
	 *
	 * @param {boolean} isEditing
	 * @return {void}
	 */
	private toggleEditQuestion(isEditing: boolean): void {
		this.isQuestionEditing = isEditing;
		this.$emit('editing-change', isEditing || this.isOptionEditing);
	}

	/**
	 * Set option editing flag to prevent locale change
	 *
	 * @param {boolean} isEditing
	 * @return {void}
	 */
	private toggleOptionEditing(isEditing: boolean): void {
		this.isOptionEditing = isEditing;
		this.$emit('editing-change', isEditing || this.isQuestionEditing);
	}

	/**
	 * Add a new empty question card and set edit mode
	 *
	 * @return {void}
	 */
	private addQuestionCard(): void {
		this.mutableCustomQuestions.unshift({
			question: '',
			options: [],
			question_type: 'select_one',
			type: this.type,
			required: false,
			is_internal: false,
			order: this.mutableCustomQuestions.length
		});

		this.isQuestionEditing = true;
		this.$emit('editing-change', true);
	}

	/**
	 * Save a question (create or update) via the dedicated API
	 *
	 * @param {CustomQuestion} question
	 * @return {Promise<void>}
	 */
	private async saveQuestion(question: CustomQuestion): Promise<void> {
		this.loading.save = true;

		try {
			if (!question.id) {
				await this.createCustomQuestion({
					question: question.question,
					question_type: question.question_type,
					type: this.type,
					options: (question as QuestionWithOptions).options || [],
					required: question.required,
					is_internal: question.is_internal,
					localization: question.localization
				});
			}
			else {
				await this.updateCustomQuestionAction({
					...question,
					type: this.type
				});
			}
			this.resetEditingState();
			Toast.success(this.$t('settings.custom_questions.save_success'));
		}
		catch (errorMessage) {
			Toast.error(this.$t('settings.custom_questions.save_error', { errorMessage }));
		}
		finally {
			this.loading.save = false;
			this.mutableCustomQuestions = cloneDeep(this.questions);
		}
	}

	/**
	 * Delete a question via the dedicated API
	 *
	 * @param {number} index
	 * @return {Promise<void>}
	 */
	private async deleteQuestion(index: number): Promise<void> {
		const question = this.mutableCustomQuestions[index];
		this.loading.deleteIndex = index;

		try {
			await this.deleteCustomQuestionAction(question.id!);
			this.resetEditingState();
			Toast.success(this.$t('settings.custom_questions.delete_success'));
		}
		catch (errorMessage) {
			Toast.error(this.$t('settings.custom_questions.delete_error', { errorMessage }));
		}
		finally {
			this.loading.deleteIndex = -1;
			this.mutableCustomQuestions = cloneDeep(this.questions);
		}
	}

	/**
	 * Reset editing state after a successful save or delete
	 *
	 * @return {void}
	 */
	private resetEditingState(): void {
		this.isQuestionEditing = false;
		this.isOptionEditing = false;
		this.$emit('editing-change', false);
	}

	/**
	 * Handle drag-and-drop reorder. Updates the moved question's order via API.
	 *
	 * @param {{ newIndex: number; oldIndex: number }} event
	 * @return {Promise<void>}
	 */
	private async onDragEnd(event: { newIndex: number; oldIndex: number }): Promise<void> {
		const { newIndex, oldIndex } = event;
		if (newIndex === oldIndex) {
			return;
		}

		this.loading.reorderIndex = newIndex;
		const movedQuestion = this.mutableCustomQuestions[newIndex];
		try {
			await this.updateCustomQuestionAction({
				...movedQuestion,
				order: newIndex
			});
		}
		catch (errorMessage) {
			await this.fetchCustomQuestions();
			Toast.error(this.$t('settings.custom_questions.reorder_error', { errorMessage }));
		}
		finally {
			this.loading.reorderIndex = -1;
			this.mutableCustomQuestions = cloneDeep(this.questions);
		}
	}
}
